import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchOrgDetail, type OrgDetail, type OrgsReadSdk } from '@/lib/clients/vault/orgs';

export interface OrgDetailState {
  detail: OrgDetail | null;
  loading: boolean;
  error: string | null;
  /**
   * Re-run both reads for the CURRENT selection — the post-write refresh for
   * the member-editing surface (invite/add and grant saves re-read the roster
   * through this rather than adding parallel fetch state). Re-uses the hook's
   * own effect + stale-response cancellation by bumping its revision. A
   * reload of the SAME target keeps the previous detail mounted while the
   * re-read runs (stale-while-revalidate), so the section — and the memory
   * bank inside it — does not unmount on every committed member write; only
   * a target CHANGE null-gaps.
   */
  reload: () => void;
}

/** The effect-owned half; `reload` is appended to the returned value. */
interface DetailData {
  detail: OrgDetail | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: DetailData = { detail: null, loading: false, error: null };

/**
 * Reads the selected vault context's scopes + roster over the BEARER client
 * whenever the target (or the read instance — a silent re-mint swaps its
 * identity) changes, with stale-response cancellation. The caller resolves
 * WHICH vault: the owner entry's id for the My-vault selection (the owner
 * administers their own vault's roster — plan ruling, fix round 1), the
 * org's id for an org row. `null` means no target (no owner entry / nothing
 * selected): no reads run at all, and any previous detail is dropped.
 */
export function useOrgDetail(readSdk: OrgsReadSdk, vaultId: string | null): OrgDetailState {
  const [state, setState] = useState<DetailData>(EMPTY);
  // Bumped by `reload` only; the effect cannot distinguish it from a
  // selection change, which is the point — a re-read IS a re-read.
  const [revision, setRevision] = useState(0);
  // The vault the CURRENT detail belongs to. A re-read of the SAME target
  // (a `reload()` revision bump, or a silent re-mint swapping the read
  // instance's identity) keeps the previous detail rendered while the fetch
  // runs — the null-gap exists to drop ANOTHER vault's detail on a selection
  // change, and re-using it for a reload unmounted the whole detail block
  // (memory bank included) on every member write. Held in a ref because the
  // loading-state write happens inside the effect, which cannot read state
  // without a stale closure.
  const detailForRef = useRef<string | null>(null);

  useEffect(() => {
    if (vaultId === null) {
      detailForRef.current = null;
      // Identity-check so the common case (already empty) does not re-render.
      setState((s) => (s === EMPTY ? s : EMPTY));
      return;
    }
    const keep = detailForRef.current === vaultId;
    let cancelled = false;
    setState(keep
      // Same target: hold the previous detail up while it re-validates.
      ? (s) => ({ detail: s.detail, loading: true, error: null })
      : { detail: null, loading: true, error: null });
    void (async () => {
      try {
        const detail = await fetchOrgDetail(readSdk, vaultId);
        if (!cancelled) {
          detailForRef.current = vaultId;
          setState({ detail, loading: false, error: null });
        }
      } catch (e) {
        if (!cancelled) {
          // A failed read claims nothing: the detail drops even when a keep
          // was holding it up, so the section shows the failure, not a
          // silently stale roster.
          setState({ detail: null, loading: false, error: e instanceof Error ? e.message : String(e) });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [readSdk, vaultId, revision]);

  const reload = useCallback(() => { setRevision((r) => r + 1); }, []);

  return { ...state, reload };
}
